import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { gql } from "@apollo/client";
import { BillingCurrency, ExchangeRateSource } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { ExchangeRate } from "../collections/exchange-rate";
import type { RxDBCollections } from "../provider";
import {
  SyncExchangeRatePullDocument,
  type WholeSyncableExchangeRateFragment,
} from "./__generated__/exchange-rate.gql";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicateExchangeRate = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<ExchangeRate>;
}) => {
  const enabled = useIsExchangeRatePullEnabled();

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncableExchangeRateFragment> | undefined
  >(() => {
    if (!enabled) return undefined;
    return {
      query: SyncExchangeRatePullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { hours: 1 },
    };
  }, [enabled]);

  return useGqlReplication<
    ExchangeRate,
    WholeSyncableExchangeRateFragment,
    null
  >({
    db,
    collection,
    pull,
    mapGqlToRx: mapGqlExchangeRateToRxExchangeRate,
    batchSize: 100,
    metadataLocalStorageKeySuffix: "exchange-rate",
  });
};

export const useIsExchangeRatePullEnabled = () => {
  const [multiCurrencyFlag] = useFeatureFlag<boolean>(
    featureFlags.billing.multiCurrency,
  );
  return Boolean(multiCurrencyFlag);
};

export const EXCHANGE_RATE_CURRENCY_GQL_TO_RX: Record<
  BillingCurrency,
  ExchangeRate["baseCurrency"]
> = {
  [BillingCurrency.Ghs]: "GHS",
  [BillingCurrency.Kes]: "KES",
  [BillingCurrency.Lrd]: "LRD",
  [BillingCurrency.Ngn]: "NGN",
  [BillingCurrency.Pkr]: "PKR",
  [BillingCurrency.Usd]: "USD",
};

export const EXCHANGE_RATE_CURRENCY_RX_TO_GQL: Record<
  ExchangeRate["baseCurrency"],
  BillingCurrency
> = {
  GHS: BillingCurrency.Ghs,
  KES: BillingCurrency.Kes,
  LRD: BillingCurrency.Lrd,
  NGN: BillingCurrency.Ngn,
  PKR: BillingCurrency.Pkr,
  USD: BillingCurrency.Usd,
};

const EXCHANGE_RATE_SOURCE_GQL_TO_RX: Record<
  ExchangeRateSource,
  ExchangeRate["source"]
> = {
  [ExchangeRateSource.Cbl]: "CBL",
};

export const mapGqlExchangeRateToRxExchangeRate = (
  rate: WholeSyncableExchangeRateFragment,
): WithDeleted<ExchangeRate> => ({
  id: rate.id,
  baseCurrency: EXCHANGE_RATE_CURRENCY_GQL_TO_RX[rate.baseCurrency],
  quoteCurrency: EXCHANGE_RATE_CURRENCY_GQL_TO_RX[rate.quoteCurrency],
  buying: rate.buying,
  selling: rate.selling,
  mid: rate.mid,
  effectiveDate: rate.effectiveDate,
  fetchedAt: rate.fetchedAt,
  source: EXCHANGE_RATE_SOURCE_GQL_TO_RX[rate.source],
  sourceUrl: rate.sourceUrl,
  meta: {
    createdAt: rate.meta.createdAt,
    createdByUserId: rate.meta.createdByUserId,
    updatedAt: rate.meta.updatedAt,
    updatedByUserId: rate.meta.updatedByUserId,
  },
  _deleted: rate.meta.deleted,
});

gql`
  fragment WholeSyncableExchangeRate on SyncableExchangeRate {
    id
    baseCurrency
    quoteCurrency
    buying
    selling
    mid
    effectiveDate
    fetchedAt
    source
    sourceUrl
    meta {
      createdAt
      createdByUserId
      updatedAt
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncExchangeRatePull($input: PullExchangeRateInput!) {
    pullExchangeRate(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableExchangeRate
      }
    }
  }
`;
